import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import { api } from '../api/client';
import { changePassword } from '../api/profile';
import { Spinner } from '../components/Spinner';
import { COLORS } from '../constants/theme';

interface AccountInfo {
  accountId: string;
  email: string;
  emailVerified: boolean;
  createdAt: string;
}

export function Profile() {
  const { isAuthenticated } = useAuth();
  const { addToast } = useToast();

  const [accountInfo, setAccountInfo] = useState<AccountInfo | null>(null);
  const [isLoadingInfo, setIsLoadingInfo] = useState(true);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    api.get<AccountInfo>('/api/auth/me').then(({ data }) => {
      if (data) setAccountInfo(data);
      setIsLoadingInfo(false);
    });
  }, [isAuthenticated]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const { error } = await changePassword({ currentPassword, newPassword, confirmNewPassword });
      if (error) {
        setSubmitError(error);
      } else {
        addToast('Password updated successfully', 'success');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmNewPassword('');
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 10px',
    background: COLORS.inputBg,
    border: `1px solid ${COLORS.inputBorder}`,
    borderRadius: 4,
    color: COLORS.text.primary,
    fontSize: 13,
    outline: 'none',
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 12,
    color: COLORS.text.tertiary,
    marginBottom: 4,
  };

  return (
    <div style={{ maxWidth: 480, margin: '0 auto', padding: '24px 16px' }}>
      <h1 style={{ color: COLORS.text.primary, fontSize: 20, fontWeight: 700, marginBottom: 24 }}>
        Account Profile
      </h1>

      {/* Account info section */}
      <div
        style={{
          background: COLORS.card,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 6,
          padding: '16px 20px',
          marginBottom: 20,
        }}
      >
        <h2
          style={{
            color: COLORS.text.secondary,
            fontSize: 13,
            fontWeight: 600,
            marginBottom: 12,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          Account Info
        </h2>
        {isLoadingInfo ? (
          <Spinner size="sm" />
        ) : accountInfo ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <span style={labelStyle}>Email</span>
              <span style={{ color: COLORS.text.primary, fontSize: 14 }}>{accountInfo.email}</span>
            </div>
            <div>
              <span style={labelStyle}>Member since</span>
              <span style={{ color: COLORS.text.primary, fontSize: 14 }}>
                {new Date(accountInfo.createdAt).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </span>
            </div>
            <div>
              <span style={labelStyle}>Email verification</span>
              <span
                style={{
                  display: 'inline-block',
                  padding: '2px 8px',
                  borderRadius: 4,
                  fontSize: 12,
                  fontWeight: 600,
                  background: accountInfo.emailVerified ? COLORS.successBg : COLORS.errorBg,
                  color: accountInfo.emailVerified ? COLORS.successText : COLORS.errorText,
                  border: `1px solid ${accountInfo.emailVerified ? COLORS.successBorder : COLORS.errorBorder}`,
                }}
              >
                {accountInfo.emailVerified ? 'Verified' : 'Unverified'}
              </span>
            </div>
          </div>
        ) : null}
      </div>

      {/* Change password form */}
      <div
        style={{
          background: COLORS.card,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 6,
          padding: '16px 20px',
        }}
      >
        <h2
          style={{
            color: COLORS.text.secondary,
            fontSize: 13,
            fontWeight: 600,
            marginBottom: 16,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          Change Password
        </h2>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={labelStyle} htmlFor="currentPassword">Current password</label>
            <input
              id="currentPassword"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              style={inputStyle}
              autoComplete="current-password"
              required
            />
          </div>
          <div>
            <label style={labelStyle} htmlFor="newPassword">New password</label>
            <input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              style={inputStyle}
              autoComplete="new-password"
              minLength={8}
              required
            />
          </div>
          <div>
            <label style={labelStyle} htmlFor="confirmNewPassword">Confirm new password</label>
            <input
              id="confirmNewPassword"
              type="password"
              value={confirmNewPassword}
              onChange={(e) => setConfirmNewPassword(e.target.value)}
              style={inputStyle}
              autoComplete="new-password"
              required
            />
          </div>

          {submitError && (
            <div
              style={{
                padding: '8px 12px',
                background: COLORS.errorBg,
                border: `1px solid ${COLORS.errorBorder}`,
                borderRadius: 4,
                color: COLORS.errorText,
                fontSize: 13,
              }}
            >
              {submitError}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              padding: '8px 16px',
              background: isSubmitting ? COLORS.inputBg : COLORS.link,
              color: isSubmitting ? COLORS.text.tertiary : '#fff',
              border: 'none',
              borderRadius: 4,
              fontSize: 13,
              fontWeight: 600,
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              alignSelf: 'flex-start',
            }}
          >
            {isSubmitting ? 'Updating...' : 'Change Password'}
          </button>
        </form>
      </div>
    </div>
  );
}
